/**
 * Bare "Salem" is geocoded to Salem, Massachusetts, so the driving
 * route from Tamil Nadu comes back with no result. The city on these
 * pages is Salem, Tamil Nadu. The display name stays Salem.
 */
const MATRIX_PLACE = {
  salem: 'Salem, Tamil Nadu, India',
};

export const toDistanceMatrixPlace = (place) => {
  const value = String(place || '').trim();
  if (!value) return value;
  return MATRIX_PLACE[value.toLowerCase()] || value;
};

const cache = new Map();

const cacheKey = (origin, destination) => (
  `${String(origin || '').trim().toLowerCase()}\n${String(destination || '').trim().toLowerCase()}`
);

const toMetrics = (element) => {
  if (element?.status !== 'OK' || !element.distance?.value) return null;
  return {
    distanceKm: Math.round((element.distance.value / 1000) * 10) / 10,
    durationText: element.duration?.text || null,
  };
};

const remember = (origin, destination, metrics) => {
  if (!metrics) return;
  cache.set(cacheKey(origin, destination), metrics);
};

const matrixService = () => {
  if (!window.google?.maps?.DistanceMatrixService) return null;
  return new window.google.maps.DistanceMatrixService();
};

const requestMatrix = (origin, destinations) => new Promise((resolve) => {
  const service = matrixService();
  if (!service) {
    resolve(null);
    return;
  }

  let settled = false;
  const finish = (elements) => {
    if (settled) return;
    settled = true;
    resolve(elements);
  };

  try {
    const pending = service.getDistanceMatrix(
      {
        origins: [toDistanceMatrixPlace(origin)],
        destinations: destinations.map(toDistanceMatrixPlace),
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        finish(status === 'OK' ? (response?.rows?.[0]?.elements || null) : null);
      },
    );
    if (pending && typeof pending.then === 'function') {
      pending
        .then((response) => finish(response?.rows?.[0]?.elements || null))
        .catch(() => finish(null));
    }
  } catch (error) {
    console.error('Route metrics error:', error);
    finish(null);
  }
});

/** Same Distance Matrix lookup the booking form uses. Successful pairs are cached. */
export const getRouteMetrics = async (origin, destination) => {
  const key = cacheKey(origin, destination);
  if (cache.has(key)) return cache.get(key);
  try {
    const elements = await requestMatrix(origin, [destination]);
    const metrics = toMetrics(elements?.[0]);
    remember(origin, destination, metrics);
    return metrics;
  } catch (error) {
    console.error('Route metrics error:', error);
    return null;
  }
};

export const readCachedRouteMetrics = (origin, destination) => (
  cache.get(cacheKey(origin, destination)) || null
);

/**
 * One Distance Matrix request per origin, shared across carousel cards.
 * Does not refetch a pair that already succeeded.
 */
export const prefetchRouteMetrics = async (pairs) => {
  if (!matrixService()) return false;

  const groups = new Map();
  pairs.forEach(([origin, destination]) => {
    if (!origin || !destination || cache.has(cacheKey(origin, destination))) return;
    const list = groups.get(origin) || [];
    if (!list.includes(destination)) list.push(destination);
    groups.set(origin, list);
  });

  for (const [origin, destinations] of groups) {
    for (let index = 0; index < destinations.length; index += 10) {
      const chunk = destinations.slice(index, index + 10);
      try {
        const elements = await requestMatrix(origin, chunk);
        if (!elements) continue;
        chunk.forEach((destination, elementIndex) => {
          remember(origin, destination, toMetrics(elements[elementIndex]));
        });
      } catch (error) {
        console.error('Route metrics error:', error);
      }
    }
  }

  return true;
};
